import { styled } from "@mui/material";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import downloadFile from "_shared/_utilities/download-file";
import { enumKeyOf } from "_shared/_utilities/enum-utils";
import { DocumentFileType } from "documents/document-file-type";
import { filesize } from "filesize";
import { useLazyDownloadMessageAttachmentQuery } from "messaging/_api/messaging-api";
import AttachmentProcessingState from "messaging/attachments/_views/attachment-processing-state";
import MessageFileInfo from "messaging/attachments/file-info/message-file-info";
import { showErrorSnackbar } from "_shared/_errors/error-snackbar";
import { ATTACHMENT_DOWNLOAD_FAILURE } from "messaging/attachments/attachment-download-failure";
import { defaultStyles, FileIcon } from "react-file-icon";

const AttachmentItemIcon = styled(ListItemIcon)(({ theme }) => ({
  marginRight: theme.spacing(1),
  maxWidth: "32px",
  minWidth: "16px",
}));
const SecondaryTextContainer = styled("span")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  gap: theme.spacing(1),
  color: theme.palette.text.secondary,
  width: "100%",
}));
/** Why the row cannot be pressed: its malware scan has not cleared the file (#6916). */
const WithheldReason = styled("span")(() => ({
  display: "block",
  fontStyle: "italic",
}));
const ForumLabel = styled("span")(() => ({
  display: "block",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

type AttachmentListItemProps = {
  /** A file from a list that spans forums, so it names the forum a download is routed through. */
  attachmentInfo: MessageFileInfo;
  /** The name of the forum the file was shared in, shown on the row. */
  forumLabel?: string;
};

/**
 * A file in the Shared files tab, which downloads when pressed. A file its malware scan has not
 * cleared cannot be pressed, and says why (#6916).
 */
export default function AttachmentListItem(props: Readonly<AttachmentListItemProps>) {
  const { attachmentInfo, forumLabel } = props;
  const forumId = attachmentInfo.forumId;
  const [downloadMessageAttachment] = useLazyDownloadMessageAttachmentQuery();

  async function handleClick() {
    try {
      if (!forumId) {
        throw new Error("Forum ID is undefined");
      }
      const response = await downloadMessageAttachment({
        forumId: forumId.value,
        fileId: attachmentInfo.id.value,
      }).unwrap();
      await downloadFile(response, attachmentInfo.name);
    } catch (error) {
      showErrorSnackbar(error, ATTACHMENT_DOWNLOAD_FAILURE);
    }
  }

  return (
    <ListItemButton disableGutters disabled={attachmentInfo.isWithheld} onClick={handleClick}>
      <AttachmentItemIcon>
        <FileIcon
          extension={enumKeyOf(DocumentFileType, attachmentInfo.name.split(".")[1]).toLocaleLowerCase()}
          {...defaultStyles[attachmentInfo.name.split(".")[1].toLocaleLowerCase() as keyof typeof defaultStyles]}
        />
      </AttachmentItemIcon>
      <ListItemText
        primary={attachmentInfo.name}
        secondary={
          <>
            {forumLabel && <ForumLabel title={forumLabel}>{forumLabel}</ForumLabel>}
            {attachmentInfo.withheldReason && (
              <WithheldReason role="status">{attachmentInfo.withheldReason}</WithheldReason>
            )}
            <SecondaryTextContainer>
              <span>{filesize(attachmentInfo.fileSize ?? 0)}</span>
              <span>{attachmentInfo.createdDate.format("MMM D, YYYY h:mm A")}</span>
            </SecondaryTextContainer>
            {/* How far the file's processing got, or its subject matters, as its message shows (#6986). */}
            <AttachmentProcessingState file={attachmentInfo} />
          </>
        }
        slotProps={{
          // A div, because the chips are not phrasing content a paragraph may hold.
          secondary: { component: "div" },
          primary: {
            sx: {
              maxWidth: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }
          },
        }}
      />
    </ListItemButton>
  );
}
